<!--
  Cliente — dettaglio.

  Impostazione: scheda a due colonne (operatività a sinistra, riepilogo a
  destra), in SOLA LETTURA di default. La modifica resta disponibile ma è
  esplicita: si entra in "modalità modifica" e si salva o si annulla.

  PACCHETTI VALET — si propongono da qui, dentro l'auto, con il modello
  nuovo (valet all'anno, durata, prezzo a rate) e il partner del pacchetto.
  Il cliente riceve un'email per confermare; finché non conferma il pacchetto
  resta «Da confermare» e l'email si può rimandare.

  NOTA DI DOMINIO — il valet non appartiene al cliente ma all'AUTO:
  a database è la tabella `minute`, legata a `car`. Un cliente con due auto ha
  due pacchetti distinti, per questo i valet sono mostrati DENTRO ogni auto e
  non in un blocco unico.
-->
<template>
  <div
    v-loading="loading"
    class="cdt"
  >
    <!-- ---------- intestazione ---------- -->
    <div class="cdt__head">
      <el-button
        class="cdt__back"
        text
        @click="$router.push('/customer')"
      >
        ‹
      </el-button>
      <span class="cdt__avatar">{{ initials }}</span>
      <div>
        <h1 class="cdt__title">
          {{ displayName }}
          <StatusPill
            :kind="customer.is_alive === false ? 'muted' : 'ok'"
            :label="customer.is_alive === false ? 'Non attivo' : 'Attivo'"
          />
        </h1>
        <div class="cdt__sub">
          {{ customer.mail }}
          <template v-if="customer.company_name">
            · P. IVA {{ customer.piva || '—' }}
          </template>
        </div>
      </div>
      <div class="cdt__spacer" />
      <div class="cdt__actions">
        <template v-if="!editing">
          <el-button @click="startEdit">
            Modifica
          </el-button>
          <el-button
            type="danger"
            plain
            @click="confirmDelete"
          >
            Elimina
          </el-button>
        </template>
        <template v-else>
          <el-button @click="cancelEdit">
            Annulla
          </el-button>
          <el-button
            type="primary"
            :loading="saving"
            @click="saveCustomer"
          >
            Salva modifiche
          </el-button>
        </template>
      </div>
    </div>

    <div class="cdt__grid">
      <!-- ================= COLONNA PRINCIPALE ================= -->
      <div class="cdt__col">
        <!-- Dati cliente -->
        <section class="cdt__card">
          <header class="cdt__cardHead">
            <h3>Dati cliente</h3>
          </header>
          <div class="cdt__cardBody">
            <div class="cdt__fields">
              <div
                v-for="f in fields"
                :key="f.key"
                class="cdt__field"
                :class="{ 'cdt__field--full': f.full }"
              >
                <span class="cdt__label">{{ f.label }}</span>
                <el-input
                  v-if="editing && !f.readonly"
                  v-model="draft[f.key]"
                  size="default"
                />
                <span
                  v-else
                  class="cdt__value"
                >{{ formatField(f) }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Auto + valet -->
        <section class="cdt__card">
          <header class="cdt__cardHead">
            <h3>Auto del cliente</h3>
            <span class="cdt__count">{{ cars.length }}</span>
            <el-button
              size="small"
              @click="openCarDialog"
            >
              Aggiungi auto
            </el-button>
          </header>
          <div class="cdt__cardBody">
            <p
              v-if="!cars.length"
              class="cdt__empty"
            >
              Nessuna auto registrata.
            </p>

            <article
              v-for="car in cars"
              :key="car.id"
              class="cdt__car"
            >
              <div class="cdt__carTop">
                <span class="cdt__carIcon">🚗</span>
                <div>
                  <div class="cdt__carName">
                    {{ [car.marca, car.modello].filter(Boolean).join(' ') || 'Auto' }}
                  </div>
                  <div class="cdt__plate">
                    {{ car.targa || '—' }}
                  </div>
                </div>
                <div class="cdt__spacer" />
                <span class="cdt__carTag">{{ transmissionLabel(car.transmission) }}</span>
              </div>

              <!-- Pacchetti valet DI QUESTA auto -->
              <div
                v-if="car.minutes && car.minutes.length"
                class="cdt__valets"
              >
                <div
                  v-for="m in car.minutes"
                  :key="m.id"
                  class="cdt__valet"
                >
                  <div class="cdt__valetTop">
                    <span class="cdt__valetPartner">{{ m.partner?.company_name || 'Pacchetto valet' }}</span>
                    <span class="cdt__valetRight">
                      <StatusPill
                        :kind="valetState(m).kind"
                        :label="valetState(m).label"
                      />
                      <!-- Niente «Reinvia email»: il pacchetto lo conferma
                           Simba da qui, al cliente non si manda nulla. -->
                      <el-button
                        v-if="m.accepted === false"
                        size="small"
                        type="primary"
                        :loading="confirming === m.id"
                        @click="confirmValet(car, m)"
                      >
                        Conferma valet
                      </el-button>
                    </span>
                  </div>
                  <div class="cdt__valetBar">
                    <span :style="{ width: valetPercent(m) + '%' }" />
                  </div>
                  <div class="cdt__valetMeta">
                    <span><b>{{ m.residual_valet }}</b> di {{ m.valet }} residui</span>
                    <span>Scadenza {{ longDate(m.expiration_valet) }}</span>
                    <span>{{ valetMonths(m) }}</span>
                    <!-- Prezzo del pacchetto: imponibile + IVA, e il totale -->
                    <span>
                      <b>{{ euro(m.imponibile) }}</b> + IVA
                      <template v-if="m.tot_fattura != null">· tot. {{ euro(m.tot_fattura) }}</template>
                    </span>
                    <!-- Prezzo del venditore, fissato alla proposta (i pacchetti precedenti non ce l'hanno) -->
                    <span v-if="m.partner_imponibile != null">
                      Al cliente <b>{{ euro(m.partner_imponibile) }}</b> + IVA
                    </span>
                  </div>
                </div>
              </div>
              <p
                v-else
                class="cdt__noValet"
              >
                Nessun pacchetto valet su quest'auto.
              </p>

              <!-- proposta di un pacchetto valet -->
              <div
                v-if="proposal.carId === car.id"
                class="cdt__proposal"
              >
                <div class="cdt__proposalTitle">
                  Nuovo pacchetto valet · {{ car.targa || 'auto' }}
                </div>
                <div class="cdt__pgrid">
                  <div class="cdt__formRow">
                    <label>Partner del pacchetto</label>
                    <el-select
                      v-model="proposal.partnerId"
                      placeholder="Scegli il partner"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="p in customerPartners"
                        :key="p.id"
                        :label="p.company_name"
                        :value="p.id"
                      />
                    </el-select>
                  </div>
                </div>
                <!-- Prezzo dal listino valet del partner: sede,
                     pickup all'anno e mesi. -->
                <ValetProposal
                  :address="customer"
                  :partner-id="proposal.partnerId"
                  @change="(e) => (proposal.result = e)"
                />
                <p class="cdt__pnote">
                  Il cliente riceve un'email per confermare il pacchetto. Finché non
                  conferma resta «Da confermare» e non si può usare nelle prenotazioni.
                </p>
                <div class="cdt__pactions">
                  <span
                    v-if="proposal.tried && proposalProblem"
                    class="cdt__err"
                    role="alert"
                  >{{ proposalProblem }}</span>
                  <span class="cdt__spacer" />
                  <el-button
                    size="small"
                    @click="proposal.carId = ''"
                  >
                    Annulla
                  </el-button>
                  <el-button
                    size="small"
                    type="primary"
                    :loading="proposal.sending"
                    @click="sendProposal(car)"
                  >
                    Proponi e invia l'email
                  </el-button>
                </div>
              </div>
              <el-button
                v-else
                size="small"
                class="cdt__propose"
                @click="openProposal(car)"
              >
                + Proponi un pacchetto valet
              </el-button>
            </article>
          </div>
        </section>

        <!-- Storico booking -->
        <section class="cdt__card">
          <header class="cdt__cardHead">
            <h3>Storico booking</h3>
            <span class="cdt__count">{{ bookings.length }}</span>
          </header>
          <div class="cdt__cardBody cdt__cardBody--flush">
            <p
              v-if="!bookings.length"
              class="cdt__empty cdt__empty--pad"
            >
              Nessun booking per questo cliente.
            </p>
            <el-table
              v-else
              :data="bookings"
              style="width: 100%"
              @row-click="openBooking"
            >
              <el-table-column
                label="Data"
                width="120"
              >
                <template #default="scope">
                  <span class="cdt__num">{{ shortDate(scope.row.data) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                label="Servizio"
                min-width="180"
              >
                <template #default="scope">
                  <ServiceChip :service="serviceOf(scope.row)" />
                </template>
              </el-table-column>
              <el-table-column
                label="Partner"
                min-width="160"
              >
                <template #default="scope">
                  {{ scope.row.headquarter?.partner?.company_name || '—' }}
                </template>
              </el-table-column>
              <el-table-column
                label="Importo"
                align="right"
                width="130"
              >
                <template #default="scope">
                  <span class="cdt__num cdt__strong">{{ euro(scope.row.tot_fattura) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                label="Stato"
                width="150"
              >
                <template #default="scope">
                  <StatusPill
                    :kind="bookingState(scope.row.state).kind"
                    :label="bookingState(scope.row.state).label"
                  />
                </template>
              </el-table-column>
              <el-table-column
                width="46"
                align="right"
              >
                <template #default>
                  <span class="cdt__chev">›</span>
                </template>
              </el-table-column>
            </el-table>
            <p class="cdt__hint">
              Clicca una riga per aprire il dettaglio del booking.
            </p>
          </div>
        </section>
      </div>

      <!-- ================= COLONNA LATERALE ================= -->
      <aside class="cdt__aside">
        <section class="cdt__card">
          <header class="cdt__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="cdt__cardBody">
            <div class="cdt__kv">
              <span>Booking totali</span><b>{{ bookings.length }}</b>
            </div>
            <div class="cdt__kv">
              <span>Spesa totale</span><b>{{ euro(totalSpent) }}</b>
            </div>
            <div class="cdt__kv">
              <span>Ultimo booking</span><b>{{ shortDate(lastBookingDate) }}</b>
            </div>
            <div class="cdt__kv">
              <span>Auto registrate</span><b>{{ cars.length }}</b>
            </div>
            <div class="cdt__kv">
              <span>Valet residui</span>
              <b :class="{ 'cdt__accent': totalValet > 0 }">{{ totalValet }}</b>
            </div>
          </div>
        </section>
      </aside>
    </div>

    <!-- ---------- nuova auto ---------- -->
    <el-dialog
      v-model="carDialog"
      title="Aggiungi auto"
      width="440px"
    >
      <div class="cdt__form">
        <div class="cdt__formRow">
          <label>Marca</label>
          <el-input
            v-model="newCar.marca"
            placeholder="es. BMW"
          />
        </div>
        <div class="cdt__formRow">
          <label>Modello</label>
          <el-input
            v-model="newCar.modello"
            placeholder="es. Serie 3"
          />
        </div>
        <div class="cdt__formRow">
          <label>Targa</label>
          <el-input
            v-model="newCar.targa"
            placeholder="es. FA123ZZ"
          />
        </div>
        <div class="cdt__formRow">
          <label>Intestatario</label>
          <el-input
            v-model="newCar.intestatario"
            placeholder="Intestatario del veicolo"
          />
        </div>
        <div class="cdt__formRow">
          <label>Cambio</label>
          <el-select
            v-model="newCar.transmission"
            style="width: 100%"
          >
            <el-option
              label="Automatico"
              value="automatic"
            />
            <el-option
              label="Manuale"
              value="manual"
            />
          </el-select>
        </div>
      </div>
      <template #footer>
        <el-button @click="carDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="carSaving"
          @click="saveCar"
        >
          Aggiungi
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import customerAPI from '../../services/customerAPI.js';
import carAPI, { serverMessage } from '../../services/carAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';
import ServiceChip from '../../components/ui/ServiceChip.vue';
import ValetProposal from '../../components/customer/ValetProposal.vue';
import { minutePayload } from '../../services/valetAPI.js';

// Campi anagrafici mostrati. `full` = occupa l'intera riga (l'indirizzo sta
// tutto insieme: via, CAP e città sono un'unica informazione per chi legge).
// `companyOnly` = ha senso solo per le aziende: su un privato sarebbe una
// riga vuota, quindi non si mostra affatto.
const FIELDS = [
  { key: 'name', label: 'Nome' },
  { key: 'surname', label: 'Cognome' },
  { key: 'company_name', label: 'Ragione sociale', companyOnly: true },
  { key: 'piva', label: 'Partita IVA', companyOnly: true },
  { key: 'mail', label: 'Email' },
  { key: 'telephone_number', label: 'Telefono' },
  { key: 'address', label: 'Indirizzo', full: true, computed: 'fullAddress' },
  { key: 'date_birth', label: 'Data di nascita', format: 'date' },
];

export default {
  name: 'CustomerDetailPage',
  components: { StatusPill, ServiceChip, ValetProposal },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const customer = ref({});
    const cars = ref([]);
    const bookings = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const editing = ref(false);
    const draft = reactive({});

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await customerAPI.getCustomerById(route.params.id);
        customer.value = data || {};

        // Auto + valet in una sola chiamata (il join porta minutes e il partner).
        const carsRes = await carAPI.getCarsByCustomer(route.params.id);
        cars.value = carsRes.data || [];

        // Lo storico non deve bloccare la scheda: se fallisce resta vuoto.
        try {
          const bkRes = await customerAPI.getCustomerBookings(route.params.id);
          const body = bkRes.data;
          bookings.value = Array.isArray(body) ? body : body?.data ?? [];
        } catch (e) {
          console.warn('Storico booking non disponibile:', e?.message);
          bookings.value = [];
        }
      } catch (e) {
        console.error('Caricamento cliente fallito:', e);
        ElMessage({ message: 'Impossibile caricare il cliente', type: 'error' });
      } finally {
        loading.value = false;
      }
    };

    // ---- modifica ------------------------------------------------------
    const startEdit = () => {
      FIELDS.forEach((f) => {
        draft[f.key] = customer.value[f.key] ?? '';
      });
      editing.value = true;
    };

    const cancelEdit = () => {
      editing.value = false;
    };

    const saveCustomer = async () => {
      saving.value = true;
      try {
        // Si invia solo ciò che è realmente cambiato, più l'id.
        const payload = { id: customer.value.id };
        FIELDS.forEach((f) => {
          if (f.readonly) return;
          if (draft[f.key] !== customer.value[f.key]) payload[f.key] = draft[f.key];
        });
        await customerAPI.updCustomer(payload);
        Object.assign(customer.value, payload);
        editing.value = false;
        ElMessage({ message: 'Dati cliente aggiornati', type: 'success' });
      } catch (e) {
        console.error('Salvataggio cliente fallito:', e);
        ElMessage({ message: 'Salvataggio non riuscito', type: 'error' });
      } finally {
        saving.value = false;
      }
    };

    // ---- aggiunta auto ---------------------------------------------------
    const carDialog = ref(false);
    const carSaving = ref(false);
    const newCar = reactive({
      marca: '',
      modello: '',
      targa: '',
      intestatario: '',
      transmission: 'automatic',
    });

    const openCarDialog = () => {
      Object.assign(newCar, {
        marca: '',
        modello: '',
        targa: '',
        // di default l'auto è intestata al cliente stesso
        intestatario: displayName.value,
        transmission: 'automatic',
      });
      carDialog.value = true;
    };

    const saveCar = async () => {
      if (!newCar.marca.trim() || !newCar.modello.trim() || !newCar.targa.trim()) {
        ElMessage({ message: 'Marca, modello e targa sono obbligatori', type: 'warning' });
        return;
      }
      carSaving.value = true;
      try {
        await carAPI.addCar({
          ...newCar,
          targa: newCar.targa.trim().toUpperCase(),
          customerId: customer.value.id,
        });
        carDialog.value = false;
        ElMessage({ message: 'Auto aggiunta', type: 'success' });
        // Ricarica solo le auto: il resto della scheda non è cambiato.
        await reloadCars();
      } catch (e) {
        console.error('Aggiunta auto fallita:', e);
        ElMessage({ message: 'Impossibile aggiungere l\'auto', type: 'error' });
      } finally {
        carSaving.value = false;
      }
    };

    const reloadCars = async () => {
      const carsRes = await carAPI.getCarsByCustomer(route.params.id);
      cars.value = carsRes.data || [];
    };

    // ---- pacchetti valet -------------------------------------------
    // Prezzo dal listino valet, stesso modello del gestionale
    // partner: lo calcola `ValetProposal` (sede, pickup all'anno, mesi); qui
    // si sceglie il partner e si salva. Sul pacchetto va il prezzo Simba.
    const customerPartners = computed(() => customer.value.partners || []);

    const proposal = reactive({
      carId: '', partnerId: '', result: null, tried: false, sending: false,
    });

    const openProposal = (car) => {
      Object.assign(proposal, {
        carId: car.id,
        // Con un solo partner non c'è niente da scegliere.
        partnerId: customerPartners.value.length === 1 ? customerPartners.value[0].id : '',
        result: null,
        tried: false,
        sending: false,
      });
    };

    const proposalProblem = computed(() => {
      if (!customerPartners.value.length) return 'Il cliente non è collegato a nessun partner';
      if (!proposal.partnerId) return 'Scegli il partner del pacchetto';
      return proposal.result?.problem ?? 'Calcolo del prezzo in corso…';
    });

    // Crea il pacchetto e manda l'email di conferma. È un'email vera al
    // cliente: si chiede prima.
    const sendProposal = async (car) => {
      proposal.tried = true;
      if (proposalProblem.value) return;
      const mail = customer.value.mail || 'il cliente';
      try {
        await ElMessageBox.confirm(
          `Creare il pacchetto per l'auto ${car.targa || ''} e mandare a ${mail} l'email per confermarlo?`,
          'Proponi pacchetto valet',
          { confirmButtonText: 'Proponi e invia', cancelButtonText: 'Annulla', type: 'info' },
        );
      } catch {
        return; // annullato
      }
      proposal.sending = true;
      let created;
      try {
        const { data } = await carAPI.addMinute(minutePayload({
          ...proposal.result.data,
          carId: car.id,
          partnerId: proposal.partnerId,
        }), { skipErrorToast: true });
        created = data;
      } catch (e) {
        console.error('Pacchetto non creato:', e);
        const detail = serverMessage(e);
        ElMessage({
          message: detail ? `Pacchetto non creato: ${detail}` : 'Pacchetto non creato',
          type: 'error',
          duration: 8000,
          showClose: true,
        });
        proposal.sending = false;
        return;
      }
      try {
        const r = await carAPI.mailAddMinute(created.id);
        if (r?.data?.stato === 'reject') throw new Error('reject');
        ElMessage({ message: `Pacchetto proposto: email inviata a ${mail}.`, type: 'success' });
      } catch (e) {
        console.error('Email proposta non inviata:', e);
        ElMessage({
          message: "Pacchetto creato, ma l'email al cliente non è partita: lo puoi confermare tu con «Conferma valet».",
          type: 'warning',
          duration: 8000,
          showClose: true,
        });
      }
      proposal.carId = '';
      proposal.sending = false;
      await reloadCars();
    };

    // Conferma del pacchetto fatta da Simba al posto del cliente (per esempio
    // quando il cliente ha confermato a voce o l'email non gli arriva).
    const confirming = ref('');
    const confirmValet = async (car, m) => {
      try {
        await ElMessageBox.confirm(
          `Confermare il pacchetto valet dell'auto ${car.targa || ''} al posto del cliente? `
            + 'Diventa subito utilizzabile nelle prenotazioni, al partner viene registrata la '
            + 'provvigione e parte l\'email di avvenuta conferma.',
          'Conferma pacchetto valet',
          { confirmButtonText: 'Conferma', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch {
        return; // annullato
      }
      confirming.value = m.id;
      try {
        const r = await carAPI.confirmMinute(m.id);
        if (r?.data?.stato === 'reject') throw new Error('reject');
        ElMessage({
          message: r?.data?.messaggio === 'già confermato'
            ? 'Il pacchetto risultava già confermato.'
            : 'Pacchetto valet confermato.',
          type: 'success',
        });
        await reloadCars();
      } catch (e) {
        ElMessage({ message: 'Conferma non riuscita. Riprova tra poco.', type: 'error' });
      } finally {
        confirming.value = '';
      }
    };

    // Durata: `duration_months` sui pacchetti nuovi, `duration_years` sugli
    // storici che non hanno i mesi.
    const valetMonths = (m) => {
      const months = Number(m.duration_months) || (Number(m.duration_years) || 0) * 12;
      if (!months) return 'Durata non indicata';
      return months === 1 ? '1 mese' : `${months} mesi`;
    };

    const confirmDelete = async () => {
      try {
        await ElMessageBox.confirm(
          'Il cliente verrà eliminato. Confermi?',
          'Elimina cliente',
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch {
        return; // annullato dall'utente
      }
      try {
        await customerAPI.deleteCustomer(customer.value.id);
        ElMessage({ message: 'Cliente eliminato', type: 'success' });
        router.push('/customer');
      } catch (e) {
        console.error('Eliminazione fallita:', e);
        ElMessage({ message: 'Eliminazione non riuscita', type: 'error' });
      }
    };

    // ---- presentazione -------------------------------------------------
    // È un'azienda se ha una ragione sociale valorizzata.
    const isCompany = computed(() => !!(customer.value.company_name || '').trim());

    // I campi "solo azienda" spariscono per i privati.
    const visibleFields = computed(() =>
      FIELDS.filter((f) => !f.companyOnly || isCompany.value),
    );

    const displayName = computed(
      () =>
        customer.value.company_name ||
        [customer.value.name, customer.value.surname].filter(Boolean).join(' ') ||
        'Cliente',
    );

    const initials = computed(() => {
      const src =
        customer.value.company_name ||
        `${customer.value.name || ''} ${customer.value.surname || ''}`;
      return (
        src.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?'
      );
    });

    const fullAddress = computed(() => {
      const c = customer.value;
      const street = [c.address, c.street_number].filter(Boolean).join(' ');
      const town = [c.cap, c.city].filter(Boolean).join(' ');
      const prov = c.province ? `(${c.province})` : '';
      return [street, town, prov].filter(Boolean).join(', ') || '—';
    });

    const totalValet = computed(() =>
      cars.value.reduce(
        (sum, car) =>
          sum + (car.minutes || []).reduce((s, m) => s + (Number(m.residual_valet) || 0), 0),
        0,
      ),
    );

    const totalSpent = computed(() =>
      bookings.value.reduce((s, b) => s + (Number(b.tot_fattura) || 0), 0),
    );

    const lastBookingDate = computed(() => bookings.value[0]?.data || null);

    const euro = (v) =>
      v === null || v === undefined || v === ''
        ? '—'
        : `€ ${Number(v).toLocaleString('it-IT', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

    const shortDate = (v) =>
      v ? new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

    const longDate = (v) => (v ? new Date(v).toLocaleDateString('it-IT') : '—');

    const formatField = (f) => {
      if (f.computed === 'fullAddress') return fullAddress.value;
      const raw = customer.value[f.key];
      if (raw === null || raw === undefined || raw === '') return '—';
      return f.format === 'date' ? longDate(raw) : raw;
    };

    const transmissionLabel = (t) => {
      if (!t) return 'Cambio n/d';
      return String(t).toLowerCase().startsWith('auto') ? 'Cambio automatico' : 'Cambio manuale';
    };

    // Percentuale di valet ancora disponibili (per la barra).
    const valetPercent = (m) => {
      const tot = Number(m.valet) || 0;
      if (!tot) return 0;
      return Math.max(0, Math.min(100, (Number(m.residual_valet) / tot) * 100));
    };

    const valetState = (m) => {
      if (m.accepted === false) return { kind: 'attn', label: 'Da confermare' };
      const exp = m.expiration_valet ? new Date(m.expiration_valet) : null;
      if (exp && exp < new Date()) return { kind: 'muted', label: 'Scaduto' };
      if (!Number(m.residual_valet)) return { kind: 'muted', label: 'Esaurito' };
      return { kind: 'ok', label: 'Attivo' };
    };

    // Il servizio mostrato nello storico: i booking "restaurant" (= Notturno a
    // UI) hanno un type_booking dedicato, gli altri usano type_prenotazione.
    // Il Transfer di un partner notturno resta un Transfer.
    const serviceOf = (b) => {
      if (String(b.type_prenotazione || '').startsWith('escort')) return b.type_prenotazione;
      return b.type_booking === 'restaurant' ? 'restaurant' : b.type_prenotazione || 'pickup';
    };

    const bookingState = (state) => {
      if (state === 'end') return { kind: 'muted', label: 'Concluso' };
      if (state === 'canceled') return { kind: 'danger', label: 'Annullato' };
      if (state === 'before') return { kind: 'attn', label: 'Non ancora confermata' };
      if (state === 'accepted') return { kind: 'ok', label: 'Accettato' };
      return { kind: 'active', label: 'In corso' };
    };

    const openBooking = (row) => {
      if (row?.id) router.push(`/booking/detail/${row.id}`);
    };

    onMounted(load);

    return {
      customer, cars, bookings, loading, saving, editing, draft,
      fields: visibleFields, isCompany,
      displayName, initials, totalValet, totalSpent, lastBookingDate,
      startEdit, cancelEdit, saveCustomer, confirmDelete,
      carDialog, carSaving, newCar, openCarDialog, saveCar,
      euro, shortDate, longDate, formatField, transmissionLabel,
      valetPercent, valetState, serviceOf, bookingState, openBooking,
      customerPartners, proposal, openProposal, proposalProblem,
      sendProposal, confirming, confirmValet, valetMonths,
    };
  },
};
</script>

<style scoped>
.cdt { padding: 4px 0 32px; }

/* ---- intestazione ---- */
.cdt__head {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.cdt__back { font-size: 22px; padding: 0 6px; }
.cdt__avatar {
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--avatar-bg);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
}
.cdt__title {
  margin: 0;
  font-size: 21px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: var(--text-primary);
}
.cdt__sub { margin-top: 3px; font-size: 12.5px; color: var(--text-tertiary); }
.cdt__spacer { flex: 1; }
.cdt__actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* ---- griglia a due colonne ---- */
.cdt__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}
@media (max-width: 960px) {
  .cdt__grid { grid-template-columns: 1fr; }
}
.cdt__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.cdt__aside { position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px; }
@media (max-width: 960px) {
  .cdt__aside { position: static; }
}

/* ---- card ---- */
.cdt__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.cdt__cardHead {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--border-color);
}
.cdt__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.cdt__count {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
}
.cdt__cardBody { padding: 16px; }
.cdt__cardBody--flush { padding: 0; }

/* ---- campi anagrafici ---- */
.cdt__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 22px;
}
@media (max-width: 560px) {
  .cdt__fields { grid-template-columns: 1fr; }
}
.cdt__field--full { grid-column: 1 / -1; }
.cdt__label {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.cdt__value {
  display: block;
  margin-top: 3px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ---- auto ---- */
.cdt__car {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 13px 14px;
  background: var(--bg-secondary);
}
.cdt__car + .cdt__car { margin-top: 10px; }
.cdt__carTop { display: flex; align-items: center; gap: 12px; }
.cdt__carIcon {
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: var(--radius-md);
  display: grid;
  place-items: center;
  background: var(--bg-tertiary);
  font-size: 17px;
}
.cdt__carName { font-weight: 600; color: var(--text-primary); }
.cdt__plate {
  display: inline-block;
  margin-top: 3px;
  padding: 0 6px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary);
}
.cdt__carTag { font-size: 12px; color: var(--text-secondary); }

/* ---- valet (dentro l'auto) ---- */
.cdt__valets { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
.cdt__valet {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-primary);
  padding: 11px 12px;
}
.cdt__valetTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.cdt__valetPartner {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.cdt__valetBar {
  height: 8px;
  border-radius: 6px;
  background: var(--bg-tertiary);
  overflow: hidden;
}
.cdt__valetBar > span {
  display: block;
  height: 100%;
  border-radius: 6px;
  background: var(--svc-notturno);
}
.cdt__valetMeta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  font-size: 12.5px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}
.cdt__noValet { margin: 10px 0 0; font-size: 12.5px; color: var(--text-tertiary); }
.cdt__valetRight { display: inline-flex; align-items: center; gap: 8px; }

/* ---- proposta di un pacchetto ---- */
.cdt__propose { margin-top: 12px; }
.cdt__proposal {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cdt__proposalTitle {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.cdt__pgrid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
.cdt__pgrid :deep(.el-input-number) { width: 100%; }
@media (max-width: 560px) {
  .cdt__pgrid { grid-template-columns: 1fr; }
}
.cdt__pstats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: 12.5px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}
.cdt__pstats b { color: var(--text-primary); }
.cdt__pnote { margin: 0; font-size: 12px; line-height: 1.5; color: var(--text-tertiary); }
.cdt__pactions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.cdt__pactions .el-button + .el-button { margin-left: 0; }
.cdt__err { font-size: 12.5px; font-weight: 600; color: var(--state-attn); }

/* ---- storico ---- */
.cdt__num { font-variant-numeric: tabular-nums; }
.cdt__strong { font-weight: 700; }
.cdt__chev { color: var(--text-tertiary); font-weight: 700; }
.cdt__hint {
  margin: 0;
  padding: 9px 14px;
  border-top: 1px solid var(--border-color);
  font-size: 12px;
  color: var(--text-tertiary);
}
.cdt :deep(.el-table__row) { cursor: pointer; }

/* ---- riepilogo ---- */
.cdt__kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid var(--border-color);
  font-size: 13px;
  color: var(--text-secondary);
}
.cdt__kv:last-child { border-bottom: 0; }
.cdt__kv b { color: var(--text-primary); font-variant-numeric: tabular-nums; }
.cdt__accent { color: var(--svc-notturno); }

.cdt__empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
.cdt__empty--pad { padding: 24px 16px; text-align: center; }

/* ---- form nuova auto ---- */
.cdt__form { display: flex; flex-direction: column; gap: 12px; }
.cdt__formRow label {
  display: block;
  margin-bottom: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ---- correzioni agli override globali ----
   (il pulsante Elimina illeggibile è ora corretto alla fonte, in
   element-overrides.css, per tutte le varianti `plain` e tutte le pagine)

   header tabella: stesso trattamento chiaro della lista clienti, al posto
   del gradiente viola imposto globalmente con !important. */
.cdt :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border-color);
}
</style>
